import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import StudentHeader from '../../Components/StudentHeader';
import Footer from '../../Components/Footer';
import HttpInterceptor from '../../lib/HttpInterceptor';
import { toast } from 'react-toastify';
import { 
  FaChartLine, 
  FaArrowUp, 
  FaArrowDown, 
  FaBoxOpen, 
  FaDollarSign, 
  FaShoppingCart,
  FaPlus
} from 'react-icons/fa';

const ResellerDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentOrders, setRecentOrders] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [orderData, setOrderData] = useState([]);

  useEffect(() => {
    fetchStats();
    fetchRecentOrders();
    fetchChartData();
  }, []);

  const fetchChartData = async () => {
    try {
      // Fetch orders for last 7 days for chart
      const response = await HttpInterceptor.get('/api/resellers/orders?status=all');
      const orders = response.data.data || [];
      
      // Group by date for last 7 days
      const last7Days = Array.from({ length: 7 }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - (6 - i));
        return date.toISOString().split('T')[0];
      });

      const revenueByDate = last7Days.map(date => {
        const dayOrders = orders.filter(order => {
          const orderDate = new Date(order.createdAt).toISOString().split('T')[0];
          return orderDate === date && order.paymentStatus === 'paid';
        });
        return dayOrders.reduce((sum, order) => {
          const resellerItems = order.items.filter(item => item.resellerId);
          return sum + resellerItems.reduce((itemSum, item) => itemSum + (item.resellerPayoutAmount || 0), 0);
        }, 0);
      });

      const ordersByDate = last7Days.map(date => {
        return orders.filter(order => {
          const orderDate = new Date(order.createdAt).toISOString().split('T')[0];
          return orderDate === date;
        }).length;
      });

      setRevenueData(revenueByDate);
      setOrderData(ordersByDate);
    } catch (error) {
      console.error('Error fetching chart data:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await HttpInterceptor.get('/api/resellers/stats');
      setStats(response.data.data);
    } catch (error) {
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentOrders = async () => {
    try {
      const response = await HttpInterceptor.get('/api/resellers/orders?status=all');
      setRecentOrders(response.data.data?.slice(0, 5) || []);
    } catch (error) {
      console.error('Error fetching recent orders:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen">
        <StudentHeader />
        <main className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-white to-gray-100">
      <StudentHeader />
      
      <main className="flex-1 container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6">Reseller Dashboard</h1>

        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl shadow-lg p-6 mb-8 text-white">
          <h2 className="text-2xl font-bold mb-2">Welcome Back!</h2>
          <p className="text-purple-100">Here's an overview of your reseller performance</p>
        </div>

        {/* Stats Cards with Trends */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-purple-600">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-gray-600 text-sm font-medium">Total Revenue</h3>
              <FaDollarSign className="text-purple-600" />
            </div>
            <p className="text-3xl font-bold text-purple-600 mb-1">
              ₹{stats?.totalRevenue?.toFixed(2) || '0.00'}
            </p>
            <div className="flex items-center text-sm text-green-600">
              <FaArrowUp className="mr-1" />
              <span>All time</span>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-600">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-gray-600 text-sm font-medium">Total Orders</h3>
              <FaShoppingCart className="text-blue-600" />
            </div>
            <p className="text-3xl font-bold text-blue-600 mb-1">{stats?.totalOrders || 0}</p>
            <div className="flex items-center text-sm text-gray-500">
              <FaBoxOpen className="mr-1" />
              <span>Orders processed</span>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-yellow-600">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-gray-600 text-sm font-medium">Pending Payouts</h3>
              <FaDollarSign className="text-yellow-600" />
            </div>
            <p className="text-3xl font-bold text-yellow-600 mb-1">
              ₹{stats?.pendingPayouts?.toFixed(2) || '0.00'}
            </p>
            <Link to="/reseller/earnings" className="text-sm text-yellow-600 hover:underline">
              Request payout →
            </Link>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-600">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-gray-600 text-sm font-medium">Active Products</h3>
              <FaBoxOpen className="text-green-600" />
            </div>
            <p className="text-3xl font-bold text-green-600 mb-1">{stats?.activeProducts || 0}</p>
            <Link to="/reseller/products" className="text-sm text-green-600 hover:underline">
              Manage products →
            </Link>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Revenue Chart */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <FaChartLine className="text-purple-600" />
                Revenue (Last 7 Days)
              </h2>
            </div>
            <div className="h-64 flex items-end justify-between gap-2">
              {revenueData.map((value, index) => {
                const maxValue = Math.max(...revenueData, 1);
                const height = (value / maxValue) * 100;
                return (
                  <div key={index} className="flex-1 flex flex-col items-center">
                    <div className="w-full bg-gray-100 rounded-t relative" style={{ height: '200px' }}>
                      <div
                        className="w-full bg-gradient-to-t from-purple-600 to-purple-400 rounded-t transition-all hover:from-purple-700 hover:to-purple-500"
                        style={{ height: `${height}%` }}
                        title={`₹${value.toFixed(2)}`}
                      ></div>
                    </div>
                    <span className="text-xs text-gray-500 mt-2">
                      {new Date(Date.now() - (6 - index) * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { weekday: 'short' })}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Orders Chart */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <FaShoppingCart className="text-blue-600" />
                Orders (Last 7 Days)
              </h2>
            </div>
            <div className="h-64 flex items-end justify-between gap-2">
              {orderData.map((value, index) => {
                const maxValue = Math.max(...orderData, 1);
                const height = (value / maxValue) * 100;
                return (
                  <div key={index} className="flex-1 flex flex-col items-center">
                    <div className="w-full bg-gray-100 rounded-t relative" style={{ height: '200px' }}>
                      <div
                        className="w-full bg-gradient-to-t from-blue-600 to-blue-400 rounded-t transition-all hover:from-blue-700 hover:to-blue-500"
                        style={{ height: `${height}%` }}
                        title={`${value} orders`}
                      ></div>
                    </div>
                    <span className="text-xs text-gray-500 mt-2">
                      {new Date(Date.now() - (6 - index) * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { weekday: 'short' })}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <h2 className="text-xl font-bold mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link
              to="/reseller/products"
              className="flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <FaBoxOpen />
              <span>Manage Products</span>
            </Link>
            <Link
              to="/reseller/orders"
              className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FaShoppingCart />
              <span>View Orders</span>
            </Link>
            <Link
              to="/reseller/earnings"
              className="flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <FaDollarSign />
              <span>View Earnings</span>
            </Link>
            <Link
              to="/reseller/products"
              onClick={(e) => {
                e.preventDefault();
                navigate('/reseller/products');
                // Trigger add product modal
                setTimeout(() => {
                  const addBtn = document.querySelector('[data-add-product]');
                  if (addBtn) addBtn.click();
                }, 100);
              }}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              <FaPlus />
              <span>Add Product</span>
            </Link>
          </div>
        </div>

        {/* Performance Insights */}
        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl shadow-md p-6 mb-8 border border-purple-200">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <FaChartLine className="text-purple-600" />
            Performance Insights
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">Average Order Value</p>
              <p className="text-2xl font-bold text-purple-600">
                ₹{stats?.totalOrders > 0 ? (stats.totalRevenue / stats.totalOrders).toFixed(2) : '0.00'}
              </p>
            </div>
            <div className="bg-white rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">Commission Rate</p>
              <p className="text-2xl font-bold text-blue-600">
                {stats?.commissionRate || 10}%
              </p>
            </div>
            <div className="bg-white rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">Total Products</p>
              <p className="text-2xl font-bold text-green-600">
                {stats?.totalProducts || 0}
              </p>
            </div>
          </div>
        </div>

        {/* Recent Orders */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold mb-4">Recent Orders</h2>
          {recentOrders.length === 0 ? (
            <p className="text-gray-500">No orders yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">Order ID</th>
                    <th className="text-left p-2">Customer</th>
                    <th className="text-left p-2">Amount</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.map(order => (
                    <tr key={order._id} className="border-b">
                      <td className="p-2">#{order._id.slice(-8)}</td>
                      <td className="p-2">{order.userId?.name || 'N/A'}</td>
                      <td className="p-2">₹{order.finalAmount?.toFixed(2)}</td>
                      <td className="p-2">
                        <span className={`px-2 py-1 rounded text-sm ${
                          order.orderStatus === 'delivered' ? 'bg-green-100 text-green-700' :
                          order.orderStatus === 'shipped' ? 'bg-blue-100 text-blue-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {order.orderStatus}
                        </span>
                      </td>
                      <td className="p-2">
                        {new Date(order.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ResellerDashboard;

